import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { DivProps } from 'typings';

export const DEFAULT_COLOR = '#000000';

const FULL_HEX = /^#[0-9a-fA-F]{6}$/;
const SHORT_HEX = /^#[0-9a-fA-F]{3}$/;

/** Приводит ввод к `#rrggbb`. Возвращает `null`, если это ещё не цвет. */
export function parseHex(raw: string): string | null {
  const value = raw.trim();
  const withHash = value.startsWith('#') ? value : `#${value}`;

  if (FULL_HEX.test(withHash)) {
    return withHash.toLowerCase();
  }

  if (SHORT_HEX.test(withHash)) {
    const r = withHash[1];
    const g = withHash[2];
    const b = withHash[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return null;
}

/** Значение для свотча: он умеет только полный hex и на мусоре молча ставит чёрный. */
export function toSwatchValue(value: string | undefined): string {
  return parseHex(value ?? '') ?? DEFAULT_COLOR;
}

export interface UDColorInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  wrapperProps?: Omit<DivProps, 'children'>;
  /** Подпись свотча — по ней поле находят в тестах и читают скринридеры. */
  title?: string;
}

/**
 * Поле цвета без Formik: свотч `input[type=color]` и hex-строка рядом.
 *
 * Наружу отдаётся только валидный шестизначный hex — пока в текстовом поле
 * недописанное значение вроде `#a7`, форма держит прежний цвет. Черновик живёт
 * в локальном состоянии и на `blur` откатывается к текущему значению.
 */
const UDColorInput = (props: UDColorInputProps) => {
  const { value, onChange, onBlur, disabled, placeholder = DEFAULT_COLOR, hasError, className, wrapperProps, title } = props;

  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const emit = useCallback(
    (next: string) => {
      if (next !== value) {
        onChange?.(next);
      }
    },
    [onChange, value],
  );

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      setDraft(raw);

      const parsed = parseHex(raw);
      if (parsed && raw.trim().length >= 7) {
        emit(parsed);
      }
    },
    [emit],
  );

  const handleTextBlur = useCallback(() => {
    const parsed = parseHex(draft);

    if (parsed) {
      setDraft(parsed);
      emit(parsed);
    } else {
      setDraft(value ?? '');
    }

    onBlur?.();
  }, [draft, emit, onBlur, value]);

  const handleSwatchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value.toLowerCase();
      setDraft(next);
      emit(next);
    },
    [emit],
  );

  return (
    <Root
      {...wrapperProps}
      className={classNames(className, wrapperProps?.className, { '--error': hasError, '--disabled': disabled })}
    >
      <Swatch type="color" title={title} value={toSwatchValue(value)} onChange={handleSwatchChange} disabled={disabled} />
      <HexInput
        type="text"
        className="text subhead"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        spellCheck={false}
        autoComplete="off"
      />
    </Root>
  );
};

export default UDColorInput;

const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 12px 0 6px;
  border-radius: ${({ theme }) => theme.radii.card};
  border: 1px solid ${({ theme }) => theme.colors.LineStrong};
  background-color: ${({ theme }) => theme.colors.SurfaceCard};
  transition: border-color 0.2s ease-in-out;

  &:hover,
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
  }

  &.--error {
    border-color: ${({ theme }) => theme.colors.error};
  }

  &.--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Swatch = styled.input`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.DarkBlue100};
  border-radius: ${({ theme }) => theme.radii.control};
  background: transparent;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: ${({ theme }) => theme.radii.control};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const HexInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};
  text-transform: lowercase;

  &::placeholder {
    color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    font: inherit;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.DisabledInputColor};
    cursor: not-allowed;
  }
`;
